// Per-element font customization for public games.
// Adobe Fonts kit (Jali Latin Variable) is loaded globally in index.html;
// Google Fonts are injected on demand via loadFonts().

export const FONT_WEIGHT_LABELS = {
  300: "Léger",
  400: "Normal",
  500: "Médium",
  600: "Semi-gras",
  700: "Gras",
  800: "Très gras",
  900: "Noir",
};

export const FONT_LIBRARY = [
  { id: "jali-latin", label: "Jali Latin Variable", stack: '"jali-latin-variable", sans-serif', source: "adobe", weights: [300, 400, 500, 600, 700, 800] },
  { id: "lexend", label: "Lexend", stack: '"Lexend", sans-serif', source: "google", css: "Lexend:wght@300..800", weights: [300, 400, 500, 600, 700, 800] },
  { id: "manrope", label: "Manrope", stack: '"Manrope", sans-serif', source: "google", css: "Manrope:wght@200..800", weights: [300, 400, 500, 600, 700, 800] },
  { id: "poppins", label: "Poppins", stack: '"Poppins", sans-serif', source: "google", css: "Poppins:wght@300;400;500;600;700;800", weights: [300, 400, 500, 600, 700, 800] },
  { id: "montserrat", label: "Montserrat", stack: '"Montserrat", sans-serif', source: "google", css: "Montserrat:wght@100..900", weights: [300, 400, 500, 600, 700, 800, 900] },
  { id: "playfair", label: "Playfair Display", stack: '"Playfair Display", serif', source: "google", css: "Playfair+Display:wght@400..900", weights: [400, 500, 600, 700, 800, 900] },
  { id: "space-grotesk", label: "Space Grotesk", stack: '"Space Grotesk", sans-serif', source: "google", css: "Space+Grotesk:wght@300..700", weights: [300, 400, 500, 600, 700] },
  { id: "fredoka", label: "Fredoka", stack: '"Fredoka", sans-serif', source: "google", css: "Fredoka:wght@300..700", weights: [300, 400, 500, 600, 700] },
  { id: "baloo", label: "Baloo 2", stack: '"Baloo 2", sans-serif', source: "google", css: "Baloo+2:wght@400..800", weights: [400, 500, 600, 700, 800] },
  { id: "bebas", label: "Bebas Neue", stack: '"Bebas Neue", sans-serif', source: "google", css: "Bebas+Neue", weights: [400] },
  { id: "righteous", label: "Righteous", stack: '"Righteous", sans-serif', source: "google", css: "Righteous", weights: [400] },
  { id: "pacifico", label: "Pacifico", stack: '"Pacifico", cursive', source: "google", css: "Pacifico", weights: [400] },
];

// Game elements that support a font override.
export const FONT_ELEMENTS = ["title", "description", "prizes", "messages", "buttons", "cta"];

export function getFont(id) {
  if (!id || id === "default") return null;
  return FONT_LIBRARY.find((f) => f.id === id) || null;
}

export function loadFonts(ids) {
  (ids || []).forEach((id) => {
    const f = getFont(id);
    if (!f || f.source !== "google" || !f.css) return;
    const elId = `qrise-font-${f.id}`;
    if (document.getElementById(elId)) return;
    const link = document.createElement("link");
    link.id = elId;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${f.css}&display=swap`;
    document.head.appendChild(link);
  });
}

export function loadAllFonts() {
  loadFonts(FONT_LIBRARY.map((f) => f.id));
}

// Load only the families actually used by a game's `fonts` config.
export function loadGameFonts(fonts) {
  if (!fonts) return;
  loadFonts(
    Object.values(fonts)
      .filter(Boolean)
      .map((c) => c.family)
      .filter(Boolean),
  );
}

// Returns { fontFamily, fontWeight? } for an element, or {} when unset.
export function fontStyle(fonts, element) {
  const cfg = fonts?.[element];
  if (!cfg) return {};
  const style = {};
  const f = getFont(cfg.family);
  if (f) style.fontFamily = f.stack;
  if (cfg.weight) style.fontWeight = cfg.weight;
  return style;
}

// ---- Per-element size presets ----
export const FONT_SIZES = [
  { id: "sm", label: "Petite" },
  { id: "md", label: "Normale" },
  { id: "lg", label: "Grande" },
  { id: "xl", label: "Très grande" },
];

export const FONT_SCALE = { sm: 0.85, md: 1, lg: 1.15, xl: 1.3 };

const SIZE_CLASSES = {
  title: {
    sm: "text-3xl sm:text-4xl",
    md: "text-4xl sm:text-5xl",
    lg: "text-5xl sm:text-6xl",
    xl: "text-6xl sm:text-7xl",
  },
  description: { sm: "text-sm", md: "", lg: "text-lg", xl: "text-xl" },
  prizes: {
    sm: "text-2xl sm:text-3xl",
    md: "text-[2rem] sm:text-4xl",
    lg: "text-4xl sm:text-5xl",
    xl: "text-5xl sm:text-6xl",
  },
  messages: { sm: "text-xs", md: "text-sm", lg: "text-base", xl: "text-lg" },
  buttons: { sm: "text-xs", md: "", lg: "text-base", xl: "text-lg" },
  cta: { sm: "text-xs", md: "text-sm", lg: "text-base", xl: "text-lg" },
};

// Tailwind size classes for an element based on fonts[element].size.
export function sizeClass(fonts, element) {
  const s = fonts?.[element]?.size || "md";
  const m = SIZE_CLASSES[element] || {};
  return m[s] ?? m.md ?? "";
}

// Numeric scale used by canvas/SVG game components for prize labels.
export function fontScale(fonts, element) {
  return FONT_SCALE[fonts?.[element]?.size] || 1;
}
